/**
 * Queue: every mail the intake mailboxes received, newest first.
 *
 * Server-driven. The tab (view), the filters, the search and the page are query
 * parameters of `GET /api/queue`; the answer carries one page of 50 rows, the total,
 * the per-tab counts and the backlog the date filter hides. All of that state lives in
 * the URL search params, so Back from a case restores the exact view sales left.
 *
 * Norican I2Q works on customer inquiries only (`lib/intents.ts`). The working tabs list
 * inquiries. Purchase orders are parked under their own tab "Purchase orders (parked)"
 * (view `parked`), every other kind of mail is under "Not an inquiry"; both are counted
 * and not worked on.
 *
 * Minimal redesign (12 Oct 2026): one row per case with six columns, received,
 * customer, subject, lines, the one action word and who has it. The action word
 * (`action-word.ts`) replaces the status, type and why columns; the long reason goes to
 * the case page. Three tabs are visible, "To review", "Ready to quote" (the cases that
 * can be finished now, a primary tab since the ease round of 13 Oct 2026) and "Closed";
 * the other views sit under "More". A hint over the tab row leads a new person to "Ready
 * to quote" once per browser (`nudge-once.ts`). The filters fold behind one "Filters (n)" button at every width
 * (`filters-sheet.tsx`). The backlog chips ("312 open older than 7 days", "Back for
 * you") stay: they are the way to older work. The per-area chips stay for a viewer of
 * several areas.
 *
 * Default for everyone: cases that are mine or unassigned, received today, in the
 * mailboxes of my scope, with colleagues' forwarded inquiries and with replies without
 * new lines folded under their first mail (`queue-defaults.ts`).
 *
 * Rows can be selected after "Select" (or the `x` key) and changed together in the
 * action bar at the bottom (`BulkBar`). Opening a row leaves the order of the rows for
 * "Next case" on the case page (`next-case.ts`). Keys: `j` / `k` move between rows,
 * `Enter` opens one, `x` selects it, `?` lists the keys.
 *
 * A production build never shows fixtures: an empty result renders the tab's empty
 * state and a failed read renders the error state.
 */

import { ChevronDown, FilterX, Icon, Inbox, Search, SlidersHorizontal } from "@norican/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Chip,
  ChipGroup,
  cn,
  DataGrid,
  type DataGridColumnDef,
  EmptyState,
  Input,
  Menu,
  PageHeader,
  Pagination,
  parseDate,
  Skeleton,
  Spinner,
  Tabs,
  Tooltip,
  Typography,
} from "@norican/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { ColumnCards } from "@/components/column-cards";
import { DocumentTitle } from "@/components/document-title";
import { LiveDataError } from "@/components/live-data-error";
import { NoAreaState } from "@/components/no-area-state";
import { QUEUE_SHORTCUTS, ShortcutHelpDialog } from "@/components/shortcut-help";
import { saveQueueOrder } from "@/features/case-review/next-case";
import { HelpButton } from "@/features/help";
import { actionTone, linesToDecide, queueActionWord } from "@/features/queue/action-word";
import {
  areaChipText,
  useAreaOpenCounts,
  useColleaguesOpenCount,
  useWhenIdle,
} from "@/features/queue/area-counts";
import { BulkBar } from "@/features/queue/bulk-bar";
import { filtersButtonLabel, FiltersSheet } from "@/features/queue/filters-sheet";
import {
  markNudgeSeen,
  markStartHintSeen,
  nudgeSeenBefore,
  showsStartHint,
  startHintSeen,
} from "@/features/queue/nudge-once";
import {
  ALL_TAB_LABEL,
  CLOSE_OFFER_PARAM,
  daysAgo,
  defaultAssigneeFor,
  defaultMailboxes,
  groupsThreads,
  hasAreaWorkRole,
  isAllDates,
  isDefaultView,
  lastQueueSearch,
  QUEUE_PAGE_SIZE,
  QUEUE_PRESETS,
  type QueueDefaultsInput,
  queueFiltersFromParams,
  rangeText,
  readRememberedMailboxes,
  rememberAreas,
  rememberMailboxes,
  rememberQueueSearch,
  SEARCH_MAX_LENGTH,
  showsColleagueMails,
  toAssignee,
  toAttention,
  toClosedFilter,
  TODAY_DAYS,
  toDays,
  toMailboxList,
  toView,
  withAllDates,
  withPreset,
  withQueueDefaults,
} from "@/features/queue/queue-defaults";
import { queueActionIcon, TAB_ICON } from "@/features/queue/queue-icons";
import {
  activeElementInfo,
  caseToOpenOnEnter,
  openedRowToRestore,
  rememberOpenedRow,
} from "@/features/queue/queue-keys";
import { SearchDraft } from "@/features/queue/search-draft";
import { LearnThisLink } from "@/features/training/learn-this";
import { TrainingNudge } from "@/features/training/nudge";
import { NARROW_QUERY, TOUCH_QUERY, useMediaQuery } from "@/hooks/use-media-query";
import { useShortcuts } from "@/hooks/use-shortcuts";
import {
  localNudgeOff,
  personName,
  type QueueCounts,
  type QueueView,
  useDataHealth,
  useMe,
  useOpsMetrics,
} from "@/lib/api";
import { useAreaLabels, useMyAreas } from "@/lib/areas";
import { linesCountText, STATUS_WORDS } from "@/lib/case-status";
import { useQueueCases } from "@/lib/data";
import { FEATURES } from "@/lib/features";
import { formatDateTime, usesTwelveHours } from "@/lib/format";
import { NOT_AN_INQUIRY, PARKED_TAB } from "@/lib/intents";
import { logger } from "@/lib/logger";
import { type QuoteCaseSummary } from "@/lib/mock";
import {
  addressedToText,
  CLOSE_OFFER_WORDS,
  confirmsInOneClick,
  foldedRepliesText,
  QUEUE_EMPTY_WORDS,
  QUEUE_START_HINT,
  READY_EMPTY_WORDS,
  suggestedCustomerText,
} from "@/lib/sales-flow";
import { track } from "@/lib/usage";
import { searchShape, usageArea } from "@/lib/usage-events";
import { useTrackOnce } from "@/lib/usage-react";
import {
  CLOSED_FILTER_LABELS,
  closedKindSentence,
  closedOnText,
  formatDay,
  senderBadgeLabel,
} from "@/lib/work-labels";

const SEARCH_DEBOUNCE_MS = 400;
/** Tab of the inquiries the engine failed on (sim360 P17). */
const COULD_NOT_PROCESS_TAB = "Could not process";
/** sim360 P32: portal and tender notices (purchasing portals, tender platforms). */
const PORTAL_TAB = "Portal / tender";

interface ViewTab {
  view: QueueView;
  label: string;
  count: keyof QueueCounts;
  emptyTitle: string;
  emptyDescription: string;
}

const WORK_TAB: ViewTab = {
  view: "work",
  label: "To review",
  count: "work",
  emptyTitle: "Nothing to review",
  emptyDescription:
    "No mail with a proposal from I2Q matches the current filters. New mail appears here a few minutes after it arrives.",
};

const CLOSED_TAB: ViewTab = {
  view: "closed",
  label: "Closed",
  count: "closed",
  emptyTitle: "No closed mail",
  emptyDescription:
    "Under the current filters, no case is done, not for us, or closed by a sent quotation.",
};

/**
 * The cases that can be finished now (ease round, 13 Oct 2026): the stored state
 * `draft-ready` on an open case, the rows the server flags `can_finish_now`.
 */
const READY_TAB: ViewTab = {
  view: "ready",
  label: STATUS_WORDS.ready,
  count: "ready",
  emptyTitle: READY_EMPTY_WORDS.title,
  emptyDescription: READY_EMPTY_WORDS.text,
};

/** Every view of the queue, in the order of the former tab row. */
export const VIEW_TABS: readonly ViewTab[] = [
  WORK_TAB,
  READY_TAB,
  {
    view: "review",
    label: STATUS_WORDS.check,
    count: "review",
    emptyTitle: "Nothing needs a check",
    emptyDescription:
      "No open case under the current filters has a line or a check waiting for a person.",
  },
  {
    view: "unidentified",
    label: STATUS_WORDS.customer,
    count: "unidentified",
    emptyTitle: "No customer to confirm",
    emptyDescription: "Every inquiry under the current filters has a confirmed customer.",
  },
  {
    view: "replied",
    label: STATUS_WORDS.replied,
    count: "replied",
    emptyTitle: "No replied mail",
    emptyDescription:
      "No case under the current filters has a reply from the shared mailbox without a quotation.",
  },
  {
    view: "waiting-customer",
    label: STATUS_WORDS.waiting,
    count: "waiting_customer",
    emptyTitle: "Nothing is waiting for a customer",
    emptyDescription: "No case under the current filters was set to waiting for the customer.",
  },
  CLOSED_TAB,
  {
    view: "not-rfq",
    label: NOT_AN_INQUIRY,
    count: "not_rfq",
    emptyTitle: "No mail outside the inquiries",
    emptyDescription:
      "No mail under the current filters was classified as something other than an inquiry or a purchase order.",
  },
  {
    view: "parked",
    label: PARKED_TAB,
    count: "parked",
    emptyTitle: "No purchase orders",
    emptyDescription:
      "No purchase order under the current filters. Norican I2Q counts purchase orders but does not work on them.",
  },
  {
    view: "waiting",
    label: STATUS_WORDS.waitingForI2Q,
    count: "waiting",
    emptyTitle: "Nothing is waiting",
    emptyDescription: "I2Q has read every mail under the current filters.",
  },
  {
    view: "all",
    label: ALL_TAB_LABEL,
    count: "all",
    emptyTitle: "No mail yet",
    emptyDescription:
      "No mail matches the current filters. The mail import fills this list every few minutes.",
  },
  {
    view: "could-not-process",
    label: COULD_NOT_PROCESS_TAB,
    count: "could_not_process",
    emptyTitle: "Nothing failed",
    emptyDescription:
      "I2Q read every inquiry under the current filters. A mail it could not read is tried again up to three times and is never closed while it fails.",
  },
  {
    view: "portal",
    label: PORTAL_TAB,
    count: "portal",
    emptyTitle: "No portal or tender notices",
    emptyDescription:
      "No open mail under the current filters comes from a purchasing portal or tender platform, or from a customer who quotes through a portal. Reply to these in the portal, not by mail.",
  },
];

/**
 * The three tabs always on screen; every other view is under "More". "Ready to quote" is
 * the second one since the ease round: a new person starts with cases that go through.
 */
export const PRIMARY_TABS: readonly ViewTab[] = [WORK_TAB, READY_TAB, CLOSED_TAB];
export const MORE_TABS: readonly ViewTab[] = VIEW_TABS.filter((tab) => !PRIMARY_TABS.includes(tab));

/** The six columns of a row, in order (the selection column comes first while selecting). */
export const QUEUE_COLUMN_IDS = [
  "received",
  "customer",
  "subject",
  "lines",
  "action",
  "who",
] as const;

const nf = new Intl.NumberFormat(undefined);

/** Shown text of a "Back for you" list without rows. */
export const BACK_FOR_YOU_EMPTY =
  "Nothing came back to you yet. Cases come back here when a customer you confirmed writes again or a waiting case is due.";

/** The Who cell of a case nobody took. */
export const NOBODY = "Nobody";

/** ISO timestamp → local "07 Oct" and "14:32", or empty strings when unknown. */
function formatReceived(iso: string): { day: string; time: string; full: string } {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) {
    return { day: "", time: "", full: "" };
  }
  return {
    day: date.toLocaleDateString(undefined, { day: "2-digit", month: "short" }),
    time: date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: usesTwelveHours(Intl.DateTimeFormat().resolvedOptions().locale) ? "h12" : "h23",
    }),
    full: formatDateTime(iso),
  };
}

/** "Sales-DK-GA@norican.com" → "Sales-DK"; any other address loses only its domain. */
function shortMailbox(address: string): string {
  return address.replace(/-GA@norican\.com$/i, "").replace(/@.*$/, "");
}

/** The area of a row under the customer, for a viewer of a whole area: its name, or "No area yet". */
function AreaCaption({ code }: { code: string | null }) {
  const areas = useAreaLabels();
  const label = areas.labelWithState(code);
  return (
    <span className="text-fg-subtle block truncate text-xs" title={label}>
      {label}
    </span>
  );
}

/** Dimmed placeholder for a cell without a value. */
function Blank() {
  return <span className="text-fg-subtle">-</span>;
}

/** A bare account number reads "Account 200274"; a name stays as it is. */
function customerText(customer: string): string {
  const trimmed = customer.trim();
  return /^\d{5,8}$/.test(trimmed) ? `Account ${trimmed}` : trimmed;
}

/** "Anna" from "Anna Jensen" or "Jensen, Anna"; the Who column holds one name. */
export function givenName(name: string): string {
  const trimmed = name.trim();
  if (trimmed.includes(",")) {
    return trimmed.slice(trimmed.indexOf(",") + 1).trim() || trimmed;
  }
  return trimmed.split(/\s+/)[0] ?? trimmed;
}

/**
 * The Lines cell: "3/4 found" with "1 to decide" under it, and the whole sentence for
 * the tooltip. Null without lines or without a found count (an older answer).
 */
export function queueLinesCell(row: {
  lineCount: number | null;
  linesAuto?: number | null;
  linesFound?: number | null;
}): { short: string; toDecide: string | null; full: string } | null {
  const total = row.lineCount;
  if (total == null || total === 0 || row.linesFound == null) {
    return null;
  }
  const found = Math.min(row.linesFound, total);
  const open = linesToDecide(row);
  const lines = total === 1 ? "line" : "lines";
  return {
    short: `${found}/${total} found`,
    toDecide: open > 0 ? `${open} to decide` : null,
    full:
      open > 0
        ? `${found} of ${total} ${lines} found, ${open} to decide`
        : `${found} of ${total} ${lines} found, all sure`,
  };
}

/**
 * The customer line of a row: the name, and a muted note "to confirm", "not identified"
 * or "Internal". A suggested customer shows with "to confirm". Without a customer the
 * server sends a stand-in in `customer` and says so (`customerStandIn`): the sender's
 * company as its mail domain, never the person (sim360 P17, bug hunt Q-19), or the sender
 * for a public mail provider's address. The cell then adds "not identified". A resolved
 * customer whose name equals the sender's display name stays the customer (review of the
 * hunt branch, F6). A colleague's mail names the colleague with "Internal". The person
 * stays in the tooltip.
 */
export function customerCell(
  row: Pick<
    QuoteCaseSummary,
    | "customer"
    | "customerStandIn"
    | "customerToConfirm"
    | "suggestedCustomers"
    | "workStatus"
    | "route"
    | "origin"
    | "senderName"
    | "senderEmail"
  >,
): { name: string; note: "to confirm" | "not identified" | "Internal" | null } {
  const suggestion = row.customerToConfirm ? row.suggestedCustomers?.[0] : undefined;
  if (suggestion && (row.workStatus ?? "open") === "open") {
    return { name: suggestedCustomerText(suggestion), note: "to confirm" };
  }
  const customer = (row.customer ?? "").trim();
  const sender = (row.senderName || row.senderEmail || "").trim();
  if (row.origin === "internal" && (!customer || customer === sender)) {
    return { name: sender, note: "Internal" };
  }
  if (customer && !row.customerStandIn) {
    return {
      name: customerText(customer),
      note: row.route === "ask-customer" ? "to confirm" : null,
    };
  }
  return { name: customer || sender, note: "not identified" };
}

/**
 * What the tooltip on the customer says: who sent the mail, their role, whom they
 * wrote to and whether I2Q can confirm the suggestion in one click.
 */
export function senderTipText(
  row: Pick<
    QuoteCaseSummary,
    | "senderName"
    | "senderEmail"
    | "customerRole"
    | "origin"
    | "forwardedBy"
    | "addressedTo"
    | "customerToConfirm"
    | "suggestedCustomers"
  >,
): string {
  const parts: string[] = [];
  const name = (row.senderName || "").trim();
  const email = (row.senderEmail || "").trim();
  parts.push(
    `From ${name && email && name !== email ? `${name} <${email}>` : name || email || "unknown"}`,
  );
  const role = senderBadgeLabel(row.customerRole);
  if (role) {
    parts.push(role);
  } else if (row.origin === "internal") {
    parts.push(row.forwardedBy ? `Forwarded by ${row.forwardedBy}` : "Internal");
  } else if (row.origin === "portal") {
    parts.push("Automated");
  }
  const addressed = addressedToText(row.addressedTo, personName);
  if (addressed) {
    parts.push(addressed);
  }
  const suggestion = row.customerToConfirm ? row.suggestedCustomers?.[0] : undefined;
  if (suggestion) {
    parts.push(
      confirmsInOneClick(suggestion)
        ? "Open the case to confirm the customer in one click"
        : "A guess: open the case to check the customer against the mail",
    );
  }
  return parts.join(". ");
}

/**
 * How a row of the Closed and Replied tabs was closed or answered, never the review
 * reason it had while open (360 simulation P24). Null elsewhere. Shown under the action
 * word in those tabs.
 */
export function closedWhy(
  view: QueueView,
  row: Pick<
    QuoteCaseSummary,
    "closedKind" | "closedAt" | "closeCandidate" | "workStatus" | "closedDecidedAt"
  >,
): string | null {
  if (view !== "closed" && view !== "replied") {
    return null;
  }
  const how =
    closedKindSentence(row.closedKind, row.closedAt) ??
    row.closeCandidate?.sentence ??
    (row.workStatus === "done"
      ? "Set to done by sales"
      : row.workStatus === "not_for_us"
        ? "Set to not for us by sales"
        : null);
  // Who closed it and when (P24): the evidence date above is not the close date.
  const on = view === "closed" ? closedOnText(row) : null;
  if (!how || !on) {
    return how ?? on;
  }
  // "Set to done by sales" already names who: only the day is added.
  return how.endsWith("by sales") ? `${how} on ${formatDay(row.closedDecidedAt)}` : `${how}. ${on}`;
}

/** A short text with the whole sentence in a tooltip (hover or focus). */
function WithTip({ tip, children }: { tip: string; children: React.ReactNode }) {
  return (
    <Tooltip.Root>
      <Tooltip.Trigger className="block max-w-full min-w-0 cursor-default text-left">
        {children}
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Positioner side="top">
          <Tooltip.Popup className="max-w-xs">
            {tip}
            <Tooltip.Arrow />
          </Tooltip.Popup>
        </Tooltip.Positioner>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

interface ColumnContext {
  view: QueueView;
  /** The selection column is on (after "Select" or the `x` key). */
  selecting: boolean;
  selected: ReadonlySet<string>;
  toggle: (row: QuoteCaseSummary) => void;
  onOpen: (row: QuoteCaseSummary) => void;
  focusedId: string | null;
  /** A phone: the received time sits under the subject, the cards have no Received row. */
  narrow: boolean;
  /** A viewer of a whole area (manager, executive) sees the area under the customer. */
  showArea: boolean;
  /** The viewer's address, to tone the action word of their own work. */
  viewer: string | null;
}

function buildColumns(context: ColumnContext): DataGridColumnDef<QuoteCaseSummary>[] {
  const select: DataGridColumnDef<QuoteCaseSummary>[] =
    FEATURES.bulkActions && context.selecting
      ? [
          {
            id: "select",
            header: "",
            width: 36,
            cell: (r) => (
              <Checkbox.Root
                checked={context.selected.has(r.id)}
                onCheckedChange={() => context.toggle(r)}
                aria-label={`Select ${r.subject?.trim() || r.id}`}
              />
            ),
          },
        ]
      : [];
  return [
    ...select,
    {
      id: "received",
      header: "Received",
      width: 64,
      cell: (r) => {
        const when = formatReceived(r.receivedAt);
        return when.day ? (
          <span className="block leading-tight" title={when.full}>
            <span className="text-fg block text-xs font-semibold whitespace-nowrap">
              {when.day}
            </span>
            <span className="text-fg-subtle block text-xs tabular-nums">{when.time}</span>
          </span>
        ) : (
          <Blank />
        );
      },
    },
    {
      id: "customer",
      header: "Customer",
      width: 168,
      cell: (r) => {
        const { name, note } = customerCell(r);
        return (
          <span className="block min-w-0 text-xs leading-tight">
            <WithTip tip={senderTipText(r)}>
              <span
                className={cn(
                  "line-clamp-2 break-words whitespace-normal",
                  name ? "text-fg" : "text-fg-subtle",
                )}
              >
                {name || "-"}
              </span>
            </WithTip>
            {note && <span className="text-fg-muted block">{note}</span>}
            {context.showArea && <AreaCaption code={r.area} />}
          </span>
        );
      },
    },
    {
      id: "subject",
      header: "Subject",
      // The widest column: the fixed layout shares the spare width out in proportion to
      // the widths, so the subject takes most of it. The six columns sum to 856 px and
      // fit 1366 px beside the menu without a side scroll.
      width: 320,
      cell: (r) => {
        const subject = r.subject?.trim() || "(no subject)";
        const folded = FEATURES.groupThreads ? (r.foldedReplies ?? 0) : 0;
        const inThread = folded > 0 ? folded : (r.threadCount ?? 0);
        const threadTip =
          folded > 0
            ? foldedRepliesText(folded)
            : `${inThread} more ${inThread === 1 ? "mail" : "mails"} in this thread`;
        const focused = context.focusedId === r.id;
        const when = context.narrow ? formatReceived(r.receivedAt) : null;
        return (
          <span className="block min-w-0">
            <span className="flex min-w-0 items-start gap-1.5">
              <Link
                to={`/case/${encodeURIComponent(r.id)}`}
                title={subject}
                data-queue-row={r.id}
                aria-current={focused ? "true" : undefined}
                onClick={() => context.onOpen(r)}
                className={cn(
                  "text-accent-fg-subtle line-clamp-2 min-w-0 text-xs font-semibold break-words whitespace-normal hover:underline",
                  focused && "underline",
                )}
              >
                {subject}
              </Link>
              {inThread > 0 && (
                <Badge
                  tone="neutral"
                  variant="outline"
                  size="xs"
                  className="shrink-0 tabular-nums"
                  title={threadTip ?? undefined}
                >
                  {`+${nf.format(inThread)}`}
                </Badge>
              )}
            </span>
            {when?.day && (
              <span className="text-fg-subtle mt-0.5 block text-xs" title={when.full}>
                {when.day} {when.time}
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: "lines",
      header: "Lines",
      width: 80,
      cell: (r) => {
        const cell = queueLinesCell(r);
        if (cell) {
          return (
            <WithTip tip={cell.full}>
              <span className="text-fg block text-xs whitespace-nowrap tabular-nums">
                {cell.short}
              </span>
              {cell.toDecide && (
                <span className="text-fg-muted block text-xs whitespace-nowrap tabular-nums">
                  {cell.toDecide}
                </span>
              )}
            </WithTip>
          );
        }
        // An older answer without a found count: what the counter can still say.
        const text = linesCountText({
          lineCount: r.lineCount,
          linesAuto: r.linesAuto,
          itemsFound: r.linesFound,
          customerToConfirm: r.customerToConfirm ?? r.route === "ask-customer",
        });
        return text ? (
          <span className="text-fg block text-xs whitespace-normal tabular-nums">{text}</span>
        ) : (
          <Blank />
        );
      },
    },
    {
      id: "action",
      header: "Action",
      width: 128,
      cell: (r) => {
        const word = queueActionWord(r);
        const closed = closedWhy(context.view, r);
        // The same icon the case page shows for this state; the word next to it
        // carries the meaning, so the icon is decorative and inherits the badge colour.
        const icon = queueActionIcon(word);
        return (
          // `data-can-finish`: the server flags the row as a case that can be finished now.
          <span
            className="flex min-w-0 flex-col items-start gap-0.5"
            data-can-finish={r.canFinishNow ? "" : undefined}
          >
            <Badge
              tone={actionTone(word, r.assignee, context.viewer)}
              variant="subtle"
              size="sm"
              className="h-auto max-w-full text-left whitespace-normal"
              data-action-word={word}
            >
              <span className="inline-flex items-center gap-1.5">
                {r.state === "processing" && (
                  <Spinner size="sm" label="Processing" className="size-3" />
                )}
                {icon && <Icon icon={icon} size="xs" className="shrink-0" />}
                {word}
              </span>
            </Badge>
            {closed && (
              <span
                className="text-fg-muted line-clamp-2 text-xs break-words whitespace-normal"
                title={closed}
              >
                {closed}
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: "who",
      header: "Who",
      width: 96,
      cell: (r) =>
        r.assignee ? (
          <span className="text-fg block truncate text-xs" title={personName(r.assignee)}>
            {givenName(personName(r.assignee))}
          </span>
        ) : (
          <span className="text-fg-subtle block text-xs">{NOBODY}</span>
        ),
    },
  ];
}

/** "Show only cases back for you" and its siblings, with what each shows. */
const ATTENTION_LABEL: Record<"back_for_me" | "over_1_working_day", string> = {
  back_for_me: "Back for you",
  over_1_working_day: "Waiting more than 1 working day",
};

/** "Closed without a reply seen: 745" for a chip of the Closed tab (P24). */
export function closedChipText(
  kind: "no_reply_seen" | "today",
  counts: Pick<QueueCounts, "closed_no_reply_seen" | "closed_today">,
): string {
  const n = kind === "no_reply_seen" ? counts.closed_no_reply_seen : counts.closed_today;
  return `${CLOSED_FILTER_LABELS[kind]}: ${nf.format(n ?? 0)}`;
}

/**
 * The filters set beyond the default view, for "Filters (n)". A mailbox choice counts
 * when it differs from the viewer's default set (MD-07); without the defaults input (an
 * older caller) the mailboxes are not counted.
 */
export function activeFilterCount(params: URLSearchParams, input?: QueueDefaultsInput): number {
  const keys = [
    "type",
    "area",
    "sender",
    "q",
    "internal",
    "unmapped",
    "threads",
    "addressed",
    CLOSE_OFFER_PARAM,
  ];
  let count = keys.filter((key) => params.has(key)).length;
  if (params.has("from") || params.has("to")) {
    count += 1;
  }
  const startAssignee = input?.assignee ?? "mine_or_unassigned";
  if ((params.get("assignee") ?? startAssignee) !== startAssignee) {
    count += 1;
  }
  if (input && params.has("mailbox")) {
    const chosen = [...toMailboxList(params.getAll("mailbox"))].sort().join("|");
    const defaults = [...defaultMailboxes(input)].sort().join("|");
    if (chosen !== defaults) {
      count += 1;
    }
  }
  return count;
}

export function QueuePage() {
  const [urlParams, setParams] = useSearchParams();
  const narrow = useMediaQuery(NARROW_QUERY);
  const touch = useMediaQuery(TOUCH_QUERY);
  const navigate = useNavigate();
  const { data: me } = useMe();

  // ── defaults of a fresh visit, written into the URL ───────────────────────
  const scopeKey = toMailboxList(me?.mailboxes).join("|");
  const [remembered, setRemembered] = useState(readRememberedMailboxes);
  const [today] = useState(() => new Date());
  // A manager or an executive quotes nothing: their fresh visit starts on "Everyone".
  const rolesKey = (me?.roles ?? []).join("|");
  const quotes = hasAreaWorkRole(me?.roles);
  const defaultsInput = useMemo<QueueDefaultsInput>(
    () => ({
      scopeMailboxes: scopeKey ? scopeKey.split("|") : [],
      remembered,
      assignee: defaultAssigneeFor(rolesKey ? rolesKey.split("|") : undefined),
    }),
    [scopeKey, remembered, rolesKey],
  );
  // The first-login note shows once per browser (`nudge-once.ts`); a note dismissed
  // under the older key (SA-01) stays dismissed too.
  const [showNudge] = useState(() => !localNudgeOff() && !nudgeSeenBefore());
  useEffect(() => {
    if (showNudge) {
      markNudgeSeen();
    }
  }, [showNudge]);
  // The start hint ("New here? Start with Ready to quote.") shows until it is answered once.
  const [hintSeen, setHintSeen] = useState(startHintSeen);
  const answerStartHint = () => {
    markStartHintSeen();
    setHintSeen(true);
  };
  // A bare /queue (refresh of the plain link, sidebar, logo) reopens the last view.
  const [restored] = useState(() => {
    if (urlParams.toString() !== "") {
      return null;
    }
    const last = lastQueueSearch();
    return last ? new URLSearchParams(last) : null;
  });
  const withDefaults = useMemo(
    () =>
      restored && urlParams.toString() === ""
        ? restored
        : withQueueDefaults(urlParams, defaultsInput),
    [urlParams, defaultsInput, restored],
  );
  // The same values are used for this render, so the first read is already the default view.
  const params = withDefaults ?? urlParams;
  useEffect(() => {
    if (withDefaults) {
      logger.debug("[queue] apply default view", withDefaults.toString());
      setParams(withDefaults, { replace: true });
    }
  }, [withDefaults, setParams]);
  useEffect(() => {
    if (urlParams.has("assignee")) {
      rememberQueueSearch(urlParams.toString());
    }
  }, [urlParams]);

  // ── view state, read from the URL ─────────────────────────────────────────
  const filters = queueFiltersFromParams(params, today);
  const view = toView(params.get("view"));
  const assignee = toAssignee(params.get("assignee"));
  const intents = filters.intent ?? [];
  const mailboxes = params.getAll("mailbox");
  const areaCodes = filters.area ?? [];
  const senders = filters.customer_role ?? [];
  const days = params.has("from") || params.has("to") ? null : toDays(params.get("days"));
  const receivedFrom = filters.received_from;
  const receivedTo = filters.received_to;
  const q = filters.q ?? "";
  const colleagueMails = showsColleagueMails(params);
  const repliesFolded = groupsThreads(params);
  const includeUnmapped = params.get("unmapped") === "1";
  const attention = toAttention(params.get("attention"));
  const closedFilter = toClosedFilter(params.get("closed"), view);
  const closeOffer = filters.close_offer === "not_an_inquiry";
  const page = Math.max(1, Number.parseInt(params.get("page") ?? "1", 10) || 1);

  // The search box types locally and writes the URL after a short pause; a term that
  // changes in the URL from outside (sidebar link, Back / Forward) replaces the box text
  // and cancels a pause still pending (F12, see ./search-draft).
  const [searchText, setSearchText] = useState(q);
  const writeSearchTerm = useRef<(term: string) => void>(() => {});
  const [searchDraft] = useState(
    () =>
      new SearchDraft(
        q,
        {
          setText: setSearchText,
          writeTerm: (term) => writeSearchTerm.current(term),
          schedule: (run, ms) => window.setTimeout(run, ms),
          cancel: (handle) => window.clearTimeout(handle),
        },
        SEARCH_DEBOUNCE_MS,
        SEARCH_MAX_LENGTH,
      ),
  );
  useEffect(() => () => searchDraft.dispose(), [searchDraft]);
  const addressKey = params.toString();
  useEffect(() => {
    if (searchDraft.urlChanged(q, addressKey)) {
      logger.debug("[queue] address changed outside the search box", { q });
    }
  }, [searchDraft, q, addressKey]);

  /** Apply a change to the search params. Any filter change returns to page 1. */
  function patchParams(mutate: (next: URLSearchParams) => void, keepPage = false) {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        mutate(next);
        if (!keepPage) {
          next.delete("page");
        }
        return next;
      },
      { replace: true },
    );
  }

  function setList(key: string, values: string[]) {
    patchParams((next) => {
      next.delete(key);
      values.forEach((value) => next.append(key, value));
    });
  }

  /** A param that is set or dropped by one switch. */
  function setFlag(key: string, value: string, on: boolean) {
    patchParams((next) => {
      if (on) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
    });
  }

  function onSearchChange(value: string) {
    searchDraft.type(value);
  }
  useEffect(() => {
    writeSearchTerm.current = (term: string) => {
      logger.debug("[queue] search", { q: term });
      if (term.trim()) {
        // The usage log keeps the length and "looks like a number", never a word.
        track("queue.search", { ...searchShape(term), view });
      }
      patchParams((next) => {
        if (term) {
          next.set("q", term);
        } else {
          next.delete("q");
        }
      });
    };
  });

  /** Back to the default view: dropping `assignee` makes the visit fresh again. */
  function clearFilters() {
    track("queue.filters_reset", { action: "default" });
    searchDraft.clear();
    patchParams((next) => {
      [
        "assignee",
        "days",
        "type",
        "mailbox",
        "area",
        "sender",
        "from",
        "to",
        "q",
        "internal",
        "unmapped",
        "threads",
        "attention",
        "closed",
        "addressed",
        CLOSE_OFFER_PARAM,
      ].forEach((key) => next.delete(key));
    });
  }

  /** Everyone's cases, any date: the two defaults that hide the most. */
  function widen() {
    logger.debug("[queue] widen to everyone, any date");
    track("queue.filters_reset", { action: "everyone_any_date" });
    patchParams((next) => {
      next.set("assignee", "any");
      ["days", "from", "to"].forEach((key) => next.delete(key));
    });
  }

  function setMailboxes(values: string[]) {
    setList("mailbox", values);
    // Remembered per browser for users who have a choice to make.
    if (defaultsInput.scopeMailboxes.length > 1) {
      rememberMailboxes(values);
      setRemembered(toMailboxList(values));
    }
  }

  /** The area filter; remembered per browser, the dashboard opens on it too (MA-02). */
  function setAreas(values: string[]) {
    logger.debug("[queue] areas", values);
    setList("area", values);
    rememberAreas(values);
  }

  /** Open inquiries older than 7 days: the built-in view behind the backlog chip. */
  function showOlder() {
    const preset = QUEUE_PRESETS.find((candidate) => candidate.id === "older");
    if (!preset) {
      return;
    }
    logger.debug("[queue] built-in view", preset.id);
    track("queue.chip", { chip: "older" });
    setParams(withPreset(params, preset, today), { replace: true });
  }

  /** Every open inquiry of my scope, any received date: the list behind "<n> open". */
  function showAllDates() {
    logger.debug("[queue] all open, all dates");
    track("queue.chip", { chip: "all_dates" });
    setParams(withAllDates(params), { replace: true });
  }

  /** Inside the Closed tab: closed without a reply seen, closed today, or all (P24). */
  function showClosed(kind: "no_reply_seen" | "today" | null) {
    logger.debug("[queue] closed filter", kind);
    track("queue.chip", {
      chip:
        kind === "no_reply_seen"
          ? "closed_no_reply_seen"
          : kind === "today"
            ? "closed_today"
            : "closed_all",
    });
    patchParams((next) => {
      if (kind) {
        next.set("closed", kind);
      } else {
        next.delete("closed");
      }
    });
  }

  /** Only the cases back for me, or open more than one working day: any received date. */
  function showAttention(kind: "back_for_me" | "over_1_working_day" | null) {
    logger.debug("[queue] attention", kind);
    if (kind) {
      track("queue.chip", { chip: kind });
    }
    patchParams((next) => {
      if (kind) {
        next.set("attention", kind);
        next.delete("view");
        ["days", "from", "to"].forEach((key) => next.delete(key));
      } else {
        next.delete("attention");
        if (!next.has("days") && !next.has("from") && !next.has("to")) {
          next.set("days", String(TODAY_DAYS));
        }
      }
    });
  }

  /** Another tab or a view from the More menu. */
  function chooseView(raw: string) {
    const nextView = toView(raw);
    logger.debug("[queue] view", nextView);
    track("queue.view", { view: nextView });
    patchParams((next) => {
      // the sub-filter of the Closed tab does not travel to another tab
      next.delete("closed");
      if (nextView === "work") {
        next.delete("view");
      } else {
        next.set("view", nextView);
      }
    });
  }

  const filtersActive = !isDefaultView(params, defaultsInput);
  const allDates = isAllDates(params);
  const canWiden = assignee !== "any" || receivedFrom !== undefined || receivedTo !== undefined;
  // The received dates hide open work (sales review of 12 Oct 2026, U-04): the default
  // "today" window showed "Nothing to review" with 46 open on any date, and the one way
  // out also dropped the assignee filter. The window is named and widened on its own.
  const dateWindow =
    view === "work" &&
    !attention &&
    (days != null || receivedFrom !== undefined || receivedTo !== undefined);
  const todayWindow = dateWindow && days === TODAY_DAYS;

  // ── data ──────────────────────────────────────────────────────────────────
  const {
    data: rows,
    total,
    counts,
    backlog,
    isLoading,
    isError,
    error,
    isLive,
    isRefreshing,
  } = useQueueCases(filters);
  // A search with no result, once per term and tab. The key stays in the browser: the
  // usage log gets the length of the term and "looks like a number" only.
  const emptySearch = q !== "" && !isLoading && !isRefreshing && !isError && total === 0;
  useTrackOnce(
    "queue.search_empty",
    { ...searchShape(q), view },
    emptySearch ? `${view}|${q}` : null,
  );
  const { data: health } = useDataHealth();
  // Same query (and cache entry) as the dashboard's default window.
  const { data: metrics } = useOpsMetrics(30);
  const myAreas = useMyAreas();
  const areaLabels = useAreaLabels();
  const areaInfos = areaLabels.areas;

  // The chips over all dates (MA-02, MD-08): open per area for a viewer of several
  // areas, and the open mails colleagues forwarded, which are not customer backlog.
  // Same scope as the list (mailboxes, colleagues' mails, folded replies), no dates.
  const workTabs =
    view === "work" || view === "ready" || view === "review" || view === "unidentified";
  const countBase = useMemo(
    () => ({
      mailbox: filters.mailbox,
      include_internal: filters.include_internal,
      group_threads: filters.group_threads,
    }),
    // `filters` is rebuilt on every render; its mailbox list is what the counts depend on.
    [(filters.mailbox ?? []).join("|"), filters.include_internal, filters.group_threads],
  );
  const chipsOn = FEATURES.backlogChips && workTabs;
  // The list goes first: the counts are asked once the first page has answered, the rows
  // are on the page and the browser was idle (each count costs the server over a second).
  const countsOn = useWhenIdle(chipsOn && !isLoading && !isError);
  const areaCounts = useAreaOpenCounts(areaInfos, countBase, countsOn && areaInfos.length > 1);
  const fromColleagues = useColleaguesOpenCount(countBase, countsOn);

  // ── selection, row focus and the order for "Next case" ────────────────────
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Map<string, QuoteCaseSummary>>(() => new Map());
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const paramsKey = params.toString();
  const [selectionKey, setSelectionKey] = useState(paramsKey);
  if (selectionKey !== paramsKey) {
    // Another view or page: what was selected is no longer on screen.
    setSelectionKey(paramsKey);
    setSelected(new Map());
    setFocusedId(null);
  }

  const toggle = (row: QuoteCaseSummary) =>
    setSelected((previous) => {
      const next = new Map(previous);
      if (next.has(row.id)) {
        next.delete(row.id);
      } else {
        next.set(row.id, row);
      }
      return next;
    });
  const selectIds = (ids: string[]) =>
    setSelected(new Map(rows.filter((row) => ids.includes(row.id)).map((row) => [row.id, row])));
  const stopSelecting = () => {
    setSelecting(false);
    setSelected(new Map());
  };

  const onOpen = (row: QuoteCaseSummary) => {
    logger.debug("[queue] open case", row.id);
    track(
      "queue.open_case",
      { view, position: rows.findIndex((entry) => entry.id === row.id) + 1, page },
      { caseId: row.id },
    );
    // Coming back (browser Back, the case page's queue link) focuses this row again.
    rememberOpenedRow(paramsKey, row.id);
    if (FEATURES.caseFlow) {
      saveQueueOrder({
        ids: rows.map((entry) => entry.id),
        search: params.toString(),
        offset: (page - 1) * QUEUE_PAGE_SIZE,
        total,
      });
    }
  };

  const columns = buildColumns({
    view,
    selecting,
    selected: new Set(selected.keys()),
    toggle,
    onOpen,
    focusedId,
    narrow,
    showArea: !quotes,
    viewer: me?.email ?? null,
  });

  const focusLink = (id: string) => {
    setFocusedId(id);
    const link = document.querySelector<HTMLAnchorElement>(`[data-queue-row="${CSS.escape(id)}"]`);
    link?.focus();
    link?.scrollIntoView({ block: "nearest" });
  };
  const focusRow = (step: 1 | -1) => {
    if (rows.length === 0) {
      return;
    }
    const at = focusedId ? rows.findIndex((row) => row.id === focusedId) : -1;
    const next = rows[Math.min(rows.length - 1, Math.max(0, at < 0 ? 0 : at + step))];
    if (next) {
      focusLink(next.id);
    }
  };
  // Back from a case: the focus returns to the row that was opened, once per visit.
  const restoredFocus = useRef(false);
  const rowIdsKey = rows.map((row) => row.id).join("|");
  useEffect(() => {
    if (restoredFocus.current || isLoading || rowIdsKey === "") {
      return;
    }
    restoredFocus.current = true;
    const id = openedRowToRestore(paramsKey, rowIdsKey.split("|"));
    if (id) {
      logger.debug("[queue] back on the opened row", id);
      focusLink(id);
    }
    // Runs when the rows arrive; `focusLink` and `paramsKey` are read at that moment.
  }, [rowIdsKey, isLoading]);
  useShortcuts(
    { j: "down", k: "up", x: "select", Enter: "open", "?": "help" },
    {
      down: () => focusRow(1),
      up: () => focusRow(-1),
      select: () => {
        const row = rows.find((entry) => entry.id === focusedId);
        if (row && FEATURES.bulkActions) {
          // The first `x` turns the selection column on, like the "Select" button.
          setSelecting(true);
          toggle(row);
        }
      },
      open: () => {
        // The focused row link, else the row focused with j / k. Enter on any other
        // control is declined, so that control keeps its own Enter.
        const id = caseToOpenOnEnter(
          activeElementInfo(document.activeElement),
          focusedId,
          rows.map((entry) => entry.id),
        );
        const row = id ? rows.find((entry) => entry.id === id) : undefined;
        if (!row) {
          return false;
        }
        onOpen(row);
        navigate(`/case/${encodeURIComponent(row.id)}`);
        return true;
      },
      help: () => setHelpOpen(true),
    },
    null,
    FEATURES.shortcuts,
  );

  // The mailboxes of the user's scope from `/auth/me`; the area comes from `/areas`,
  // else from the ops metrics when they are loaded. An older backend without
  // `mailboxes` falls back to the metrics list (which the backend also limits to the
  // user's areas).
  const mailboxOptions = useMemo(() => {
    const areaOf = new Map<string, string | null>(
      (metrics?.ingest.mailboxes ?? []).map((row) => [row.mailbox.toLowerCase(), row.area]),
    );
    for (const info of areaInfos) {
      for (const mailbox of [...(info.mailboxes ?? []), ...(info.service_mailboxes ?? [])]) {
        if (typeof mailbox === "string") {
          areaOf.set(mailbox.toLowerCase(), info.code);
        }
      }
    }
    const scope = scopeKey ? scopeKey.split("|") : [...areaOf.keys()];
    return scope.map((mailbox) => {
      const area = areaOf.get(mailbox);
      return {
        value: mailbox,
        label: `${shortMailbox(mailbox)}${area ? ` (${area.toUpperCase()})` : ""}`,
      };
    });
  }, [metrics, scopeKey, areaInfos]);

  // The areas of the user's scope; the value is the entity code (for Germany `wgg`).
  // A code from a shared link that `/areas` does not list stays selectable by its code.
  const areaOptions = useMemo(() => {
    const listed = areaInfos.map((info) => info.code);
    const extra = areaCodes.filter((code) => !listed.includes(code));
    return [...listed, ...extra].map((code) => ({
      value: code,
      label: areaLabels.labelWithState(code),
      searchText: `${areaLabels.label(code)} ${code}`,
    }));
    // `areaCodes` is a new array on every render: its content is the dependency.
  }, [areaLabels, areaInfos, areaCodes.join("|")]);

  const dateRange = useMemo(() => {
    const from = receivedFrom;
    const to = receivedTo ?? (days == null ? undefined : daysAgo(TODAY_DAYS, today));
    return from && to ? { from: parseDate(from), to: parseDate(to) } : null;
  }, [receivedFrom, receivedTo, days, today]);

  const activeTab = VIEW_TABS.find((tab) => tab.view === view) ?? WORK_TAB;
  const pageIds = rows.map((row) => row.id);
  const pageSelected = pageIds.filter((id) => selected.has(id)).length;
  const filterCount = activeFilterCount(params, defaultsInput);

  if (myAreas.none) {
    return (
      <div className="flex min-w-0 flex-col gap-4">
        <DocumentTitle title="Queue" />
        <PageHeader title="Queue" />
        <NoAreaState />
      </div>
    );
  }

  const showBacklog = chipsOn && backlog;
  const daysText = days != null ? `your ${days}-day view` : "the dates you set";
  // No area and no narrower mailbox set: the chips count every mailbox of the viewer (SA-02).
  const wholeScope =
    areaCodes.length === 0 &&
    defaultsInput.scopeMailboxes.length > 1 &&
    [...toMailboxList(mailboxes)].sort().join("|") ===
      [...defaultsInput.scopeMailboxes].sort().join("|");
  const scopeSuffix = wholeScope ? " (all your mailboxes)" : "";
  const countBadge = (tab: ViewTab) =>
    counts && counts[tab.count] !== undefined ? (
      <Badge
        tone={tab.view === view && !isRefreshing ? "accent" : "neutral"}
        variant="subtle"
        size="xs"
        // The counts of the previous filters while the new ones load.
        className={cn("tabular-nums", isRefreshing && "opacity-50")}
      >
        {nf.format(counts[tab.count] ?? 0)}
      </Badge>
    ) : null;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <DocumentTitle title="Queue" />
      {showNudge && <TrainingNudge />}
      <PageHeader
        title="Queue"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {!isLive && !isLoading && !isError && rows.length > 0 && (
              <Badge tone="warning" variant="outline">
                sample data (local dev)
              </Badge>
            )}
            {health && (
              // Not "waiting for I2Q": that is the tab of the viewer's own scope (SA-05).
              <Badge
                tone={health.queued_backlog > 0 ? "info" : "neutral"}
                variant="subtle"
                title="Mails of every area the import has not read yet"
              >
                I2Q backlog: {nf.format(health.queued_backlog)}{" "}
                {health.queued_backlog === 1 ? "mail" : "mails"} not read yet
              </Badge>
            )}
            <HelpButton topic="queue" />
          </div>
        }
      />

      <Tabs.Root
        value={view}
        onValueChange={(value) => chooseView(String(value))}
        className="flex min-w-0 flex-col gap-4"
      >
        {(showBacklog || attention || dateWindow || closeOffer) && (
          <div
            className="flex flex-wrap items-center gap-2"
            role="group"
            aria-label="Open work over all dates"
          >
            {dateWindow && (
              // The active window, named (U-04); its cross widens the dates alone.
              <Badge
                tone="neutral"
                variant="subtle"
                onRemove={showAllDates}
                removeLabel={QUEUE_EMPTY_WORDS.showAnyDate}
                data-queue-window={days != null ? String(days) : "range"}
              >
                {/* A one-sided range reads "received before <day>": the label says "Received" once. */}
                {QUEUE_EMPTY_WORDS.received(rangeText(params).replace(/^received /, ""))}
              </Badge>
            )}
            {attention && (
              <Badge
                tone="accent"
                variant="subtle"
                onRemove={() => showAttention(null)}
                removeLabel="Show all again"
              >
                Showing: {ATTENTION_LABEL[attention]}
              </Badge>
            )}
            {/* Hunt round 2, decision 8: the notifications and newsletters that offer
                "Close: not an inquiry", chosen in the Filters sheet. A filter only:
                nothing closes by itself. The chip names it while it is on. */}
            {closeOffer && (
              <Badge
                tone="accent"
                variant="subtle"
                onRemove={() => setFlag(CLOSE_OFFER_PARAM, "not_an_inquiry", false)}
                removeLabel="Show all again"
                data-queue-close-offer
              >
                {CLOSE_OFFER_WORDS.filter}
              </Badge>
            )}
            {showBacklog && backlog && (backlog.open_total ?? 0) > 0 && !allDates && (
              <Button
                size="sm"
                variant="outline"
                tone="accent"
                title={`Every open inquiry of this scope, also the ones outside ${daysText}`}
                onClick={showAllDates}
              >
                {nf.format(backlog.open_total ?? 0)} open, all dates{scopeSuffix}
              </Button>
            )}
            {showBacklog && backlog && (backlog.older_7d ?? 0) > 0 && (
              <Button
                size="sm"
                variant="outline"
                tone="neutral"
                title={`Open inquiries received more than 7 days ago, also outside ${daysText}`}
                onClick={showOlder}
              >
                {nf.format(backlog.older_7d ?? 0)} open older than 7 days (all dates)
              </Button>
            )}
            {showBacklog &&
              FEATURES.backForYou &&
              backlog &&
              (backlog.back_for_me ?? 0) > 0 &&
              attention !== "back_for_me" && (
                <Button
                  size="sm"
                  variant="outline"
                  tone="accent"
                  onClick={() => showAttention("back_for_me")}
                >
                  Back for you: {nf.format(backlog.back_for_me ?? 0)}
                </Button>
              )}
            {chipsOn && fromColleagues > 0 && !senders.includes("norican_colleague") && (
              <Button
                size="sm"
                variant="outline"
                tone="neutral"
                title="Open mails a Norican colleague sent or forwarded, any date: not customer backlog"
                onClick={() => {
                  logger.debug("[queue] from colleagues");
                  track("queue.chip", { chip: "from_colleagues" });
                  patchParams((next) => {
                    next.delete("sender");
                    next.append("sender", "norican_colleague");
                    next.set("assignee", "any");
                    ["days", "from", "to"].forEach((key) => next.delete(key));
                  });
                }}
              >
                From colleagues: {nf.format(fromColleagues)}
                {scopeSuffix}
              </Button>
            )}
          </div>
        )}

        {chipsOn && areaCounts.length > 1 && (
          // Open per area, all dates (MA-02): one click narrows the queue to that area.
          <ChipGroup.Root
            value={areaCodes}
            onValueChange={(value) => {
              const codes = value as string[];
              track("queue.chip", {
                chip: "area",
                areas: codes.length,
                area: codes.length === 1 ? usageArea(codes[0]) : undefined,
              });
              setAreas(codes);
            }}
            aria-label="Open inquiries per area, all dates"
            className="flex flex-wrap items-center gap-2"
          >
            {areaCounts.map((entry) => (
              <Chip
                key={entry.code}
                value={entry.code}
                size="sm"
                tone="neutral"
                title={`${entry.label}: ${entry.open == null ? "counting" : `${nf.format(entry.open)} open, all dates`}`}
              >
                {areaChipText(entry, nf)}
              </Chip>
            ))}
          </ChipGroup.Root>
        )}

        {view === "closed" && counts && counts.closed_no_reply_seen !== undefined && (
          <ChipGroup.Root
            value={closedFilter ? [closedFilter] : []}
            onValueChange={(value) => {
              const next = (value as string[])[0];
              showClosed(next === "no_reply_seen" || next === "today" ? next : null);
            }}
            aria-label="Closed cases to check"
            className="flex flex-wrap items-center gap-2"
          >
            {(["no_reply_seen", "today"] as const).map((kind) => (
              <Chip
                key={kind}
                value={kind}
                size="sm"
                tone={kind === "no_reply_seen" ? "warning" : "accent"}
              >
                {closedChipText(kind, counts)}
              </Chip>
            ))}
            {closedFilter && (
              <Button size="sm" variant="ghost" tone="neutral" onClick={() => showClosed(null)}>
                Show all closed
              </Button>
            )}
          </ChipGroup.Root>
        )}

        {showsStartHint({ view, readyCount: counts?.ready, seen: hintSeen }) && (
          // Once per browser: where a new person finds the cases that go through.
          <Alert tone="info" title={QUEUE_START_HINT.title} data-queue-start-hint>
            {QUEUE_START_HINT.text}
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                size="sm"
                tone="accent"
                onClick={() => {
                  logger.debug("[queue] start hint: show ready");
                  answerStartHint();
                  chooseView("ready");
                }}
              >
                {QUEUE_START_HINT.action}
              </Button>
              <Button size="sm" variant="ghost" tone="neutral" onClick={answerStartHint}>
                {QUEUE_START_HINT.dismiss}
              </Button>
            </div>
          </Alert>
        )}

        {/* The tab row: To review, Ready to quote, Closed, the open view from More, then search and Filters. */}
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
          <Tabs.List aria-label="Queue views" className="w-max shrink-0">
            {PRIMARY_TABS.map((tab) => {
              // Decorative: the label says it; the icon only makes the tab findable.
              const tabIcon = TAB_ICON[tab.label];
              return (
                <Tabs.Tab key={tab.view} value={tab.view} className="shrink-0">
                  <span className="inline-flex items-center gap-2 whitespace-nowrap">
                    {tabIcon && <Icon icon={tabIcon} size="xs" className="shrink-0" />}
                    {tab.label}
                    {countBadge(tab)}
                  </span>
                </Tabs.Tab>
              );
            })}
            {!PRIMARY_TABS.includes(activeTab) && (
              <Tabs.Tab value={activeTab.view} className="shrink-0">
                <span className="inline-flex items-center gap-2 whitespace-nowrap">
                  {activeTab.label}
                  {countBadge(activeTab)}
                </span>
              </Tabs.Tab>
            )}
            <Tabs.Indicator />
          </Tabs.List>
          <Menu.Root>
            <Menu.Trigger
              render={
                <Button
                  size="sm"
                  variant="ghost"
                  tone="neutral"
                  endIcon={<Icon icon={ChevronDown} size="xs" />}
                />
              }
            >
              More
            </Menu.Trigger>
            <Menu.Portal>
              <Menu.Positioner align="start" sideOffset={6}>
                <Menu.Popup>
                  {MORE_TABS.map((tab) => (
                    <Menu.Item key={tab.view} onClick={() => chooseView(tab.view)}>
                      <span className="inline-flex items-center gap-2">
                        {tab.label}
                        {countBadge(tab)}
                      </span>
                    </Menu.Item>
                  ))}
                  {FEATURES.shortcuts && !touch && (
                    <>
                      <Menu.Separator />
                      <Menu.Item onClick={() => setHelpOpen(true)}>Keyboard shortcuts</Menu.Item>
                    </>
                  )}
                </Menu.Popup>
              </Menu.Positioner>
            </Menu.Portal>
          </Menu.Root>
          <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
            <div className="min-w-0 flex-1 sm:max-w-80">
              <Input
                size="sm"
                type="search"
                value={searchText}
                maxLength={SEARCH_MAX_LENGTH}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder="Search subject, customer, sender or item number"
                aria-label="Search the queue"
                startSlot={<Icon icon={Search} size="sm" className="text-fg-subtle" />}
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              tone="neutral"
              className="shrink-0"
              startIcon={<Icon icon={SlidersHorizontal} size="sm" />}
              aria-haspopup="dialog"
              aria-expanded={filtersOpen}
              onClick={() => {
                logger.debug("[queue] filters", { open: !filtersOpen });
                track("queue.filters_open", { active: filterCount });
                setFiltersOpen(true);
              }}
            >
              {filtersButtonLabel(filterCount)}
            </Button>
          </div>
        </div>

        {q && view !== "all" && (
          <Typography variant="caption" tone="muted">
            Search covers this tab only.{" "}
            <button
              type="button"
              className="text-accent-fg-subtle cursor-pointer font-semibold hover:underline"
              onClick={() => {
                logger.debug("[queue] search hint: open All");
                patchParams((next) => next.set("view", "all"));
              }}
            >
              Open All to search every case.
            </button>
          </Typography>
        )}

        <Tabs.Panel value={view} className="min-w-0">
          {(view === "closed" || view === "replied") && (
            <LearnThisLink
              spot="queue-closed"
              lead="Closed does not mean a reply went out."
              className="mb-2"
            />
          )}
          <Card variant="outline" padding="none">
            <Card.Header className="flex flex-wrap items-center gap-2">
              {FEATURES.bulkActions &&
                rows.length > 0 &&
                !isLoading &&
                !isError &&
                (selecting ? (
                  <>
                    <Checkbox.Root
                      checked={pageSelected > 0 && pageSelected === pageIds.length}
                      indeterminate={pageSelected > 0 && pageSelected < pageIds.length}
                      onCheckedChange={(checked) =>
                        setSelected(checked ? new Map(rows.map((row) => [row.id, row])) : new Map())
                      }
                      aria-label="Select all on this page"
                      label={<span className="text-fg-muted text-xs">Select all on this page</span>}
                    />
                    <Button size="sm" variant="ghost" tone="neutral" onClick={stopSelecting}>
                      Done selecting
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    tone="neutral"
                    title="Select rows to mark them done, not for us or waiting, or to take them"
                    onClick={() => setSelecting(true)}
                  >
                    Select
                  </Button>
                ))}
              {isRefreshing && <Spinner size="sm" label="Loading" className="size-3.5" />}
              <Typography variant="caption" tone="subtle" className="ml-auto">
                {isLoading || isRefreshing
                  ? "Loading"
                  : `${nf.format(total)} ${total === 1 ? "mail" : "mails"}, newest first`}
              </Typography>
            </Card.Header>
            <Card.Body padding="none">
              {isLoading || isRefreshing ? (
                // Never the rows of the previous filters: a slow filter (6 s and more)
                // looked like an answer that matched nothing new.
                <div className="flex flex-col gap-2 p-4" aria-busy="true">
                  {Array.from({ length: 8 }, (_, index) => (
                    <Skeleton key={index} variant="rect" height={36} />
                  ))}
                </div>
              ) : isError ? (
                <LiveDataError what="the queue" error={error} className="m-4" />
              ) : rows.length === 0 ? (
                <EmptyState
                  className="py-12"
                  icon={
                    <Icon
                      icon={filtersActive ? FilterX : Inbox}
                      size="lg"
                      className="text-fg-subtle"
                    />
                  }
                  title={
                    attention === "back_for_me"
                      ? "Nothing is back for you"
                      : filtersActive
                        ? "No mail matches these filters"
                        : todayWindow
                          ? QUEUE_EMPTY_WORDS.nothingToday
                          : activeTab.emptyTitle
                  }
                  description={
                    attention === "back_for_me" ? (
                      <>
                        {BACK_FOR_YOU_EMPTY} <LearnThisLink spot="back-for-you" />
                      </>
                    ) : filtersActive ? (
                      "Nothing in this tab matches the filters and the search."
                    ) : (
                      `${activeTab.emptyDescription} This view shows mail received today that is yours or not assigned. Older open inquiries are under the backlog chips.`
                    )
                  }
                  actions={
                    filtersActive || canWiden || dateWindow ? (
                      <div className="flex flex-wrap justify-center gap-2">
                        {dateWindow && (
                          // The dates alone, first: the assignee filter stays as it is.
                          <Button
                            variant="outline"
                            tone="accent"
                            size="sm"
                            onClick={showAllDates}
                            data-queue-empty-action="any-date"
                          >
                            {QUEUE_EMPTY_WORDS.showAnyDate}
                          </Button>
                        )}
                        {(assignee !== "any" || (canWiden && !dateWindow)) && (
                          <Button
                            variant="outline"
                            tone="neutral"
                            size="sm"
                            onClick={widen}
                            data-queue-empty-action="everyone-any-date"
                          >
                            {QUEUE_EMPTY_WORDS.showEveryoneAnyDate}
                          </Button>
                        )}
                        {filtersActive && (
                          <Button
                            variant="outline"
                            tone="neutral"
                            size="sm"
                            startIcon={<Icon icon={FilterX} size="sm" />}
                            onClick={clearFilters}
                          >
                            Back to my default view
                          </Button>
                        )}
                      </div>
                    ) : undefined
                  }
                />
              ) : narrow ? (
                // A phone: one card per mail with the subject (received under it), the
                // customer, the action word and who has it; the lines stay on the case.
                <ColumnCards
                  columns={columns}
                  data={rows}
                  getRowId={(r) => r.id}
                  lead={["subject", "customer", "action"]}
                  skip={["received", "lines"]}
                  label={`${activeTab.label}, newest first`}
                />
              ) : (
                <DataGrid
                  columns={columns}
                  data={rows}
                  getRowId={(r) => r.id}
                  density="compact"
                  tableLayout="fixed"
                  aria-label={`${activeTab.label}, newest first`}
                />
              )}
            </Card.Body>
            {!isLoading && !isError && total > 0 && (
              <Card.Footer className="border-border-subtle border-t">
                <Pagination
                  page={page}
                  total={total}
                  pageSize={QUEUE_PAGE_SIZE}
                  label="Queue pages"
                  onPageChange={(nextPage) => {
                    logger.debug("[queue] page", nextPage);
                    track("queue.page", { page: nextPage });
                    patchParams((next) => {
                      if (nextPage <= 1) {
                        next.delete("page");
                      } else {
                        next.set("page", String(nextPage));
                      }
                    }, true);
                  }}
                  formatSummary={(range) =>
                    `Showing ${nf.format(range.from)} to ${nf.format(range.to)} of ${nf.format(range.total)}`
                  }
                />
              </Card.Footer>
            )}
          </Card>
        </Tabs.Panel>
      </Tabs.Root>

      {FEATURES.bulkActions && (
        <BulkBar
          rows={[...selected.values()]}
          onSelect={selectIds}
          onClear={() => setSelected(new Map())}
        />
      )}

      <FiltersSheet
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        roles={me?.roles}
        quotes={quotes}
        view={view}
        assignee={assignee}
        onAssigneeChange={(next) => {
          track("queue.filter_change", { filter: "assignee" });
          patchParams((search) => search.set("assignee", next));
        }}
        mailboxOptions={mailboxOptions}
        mailboxes={mailboxes}
        onMailboxesChange={(values) => {
          track("queue.filter_change", { filter: "mailbox" });
          setMailboxes(values);
        }}
        scopeMailboxCount={defaultsInput.scopeMailboxes.length}
        scopeAreaCount={areaInfos.length}
        areaOptions={areaOptions}
        areas={areaCodes}
        onAreasChange={(values) => {
          track("queue.filter_change", { filter: "area" });
          setAreas(values);
        }}
        senders={senders}
        onSendersChange={(values) => {
          track("queue.filter_change", { filter: "sender" });
          setList("sender", values);
        }}
        intents={intents}
        onIntentsChange={(values) => {
          track("queue.filter_change", { filter: "type" });
          setList("type", values);
        }}
        dateRange={dateRange}
        onDateRangeChange={(range) => {
          track("queue.filter_change", { filter: "dates" });
          patchParams((next) => {
            next.delete("days");
            if (range) {
              next.set("from", range.from.toString().slice(0, 10));
              next.set("to", range.to.toString().slice(0, 10));
            } else {
              next.delete("from");
              next.delete("to");
            }
          });
        }}
        days={days}
        addressedToMe={params.get("addressed") === "me_or_nobody"}
        onAddressedToMeChange={(checked) => {
          track("queue.filter_change", { filter: "addressed" });
          setFlag("addressed", "me_or_nobody", checked);
        }}
        closeOffer={closeOffer}
        onCloseOfferChange={(checked) => {
          logger.debug("[queue] close offer filter", { on: checked });
          setFlag(CLOSE_OFFER_PARAM, "not_an_inquiry", checked);
        }}
        hideColleagues={!colleagueMails}
        onHideColleaguesChange={(checked) => {
          track("queue.filter_change", { filter: "internal" });
          setFlag("internal", "0", checked);
        }}
        showReplies={!repliesFolded}
        onShowRepliesChange={(checked) => {
          track("queue.filter_change", { filter: "threads" });
          setFlag("threads", "all", checked);
        }}
        includeUnmapped={includeUnmapped}
        onIncludeUnmappedChange={(checked) => {
          track("queue.filter_change", { filter: "unmapped" });
          setFlag("unmapped", "1", checked);
        }}
        filtersActive={filtersActive}
        onReset={() => {
          clearFilters();
          setFiltersOpen(false);
        }}
      />

      {helpOpen && <ShortcutHelpDialog open onOpenChange={setHelpOpen} rows={QUEUE_SHORTCUTS} />}
    </div>
  );
}
